import {
  USER_LOCATION_PERMISSION,
  UserCurrentLocationPayload,
} from '../../../redux/user/userSlice';

export const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

const SECOND_IN_MS = 1000;

const getLocationPermissionStatus = async () => {
  try {
    const permissionStatus = await navigator.permissions.query({
      name: 'geolocation',
    });
    return permissionStatus.state as USER_LOCATION_PERMISSION;
  } catch (error) {
    console.error('Error getting permission status:', error);
    return USER_LOCATION_PERMISSION.DENIED as USER_LOCATION_PERMISSION;
  }
};

const getUserCurrentLocation =
  async (): Promise<UserCurrentLocationPayload> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        const payload = {
          permissionStatus: USER_LOCATION_PERMISSION.DENIED,
        };
        resolve(payload);
        return;
      }

      const timeoutId = setTimeout(() => {
        resolve({
          permissionStatus: USER_LOCATION_PERMISSION.DENIED,
        });
      }, 15 * SECOND_IN_MS);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);

          if (position && position.coords) {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            const payload = {
              coordinates: location,
              permissionStatus: USER_LOCATION_PERMISSION.GRANTED,
            };
            resolve(payload);
          } else {
            resolve({
              permissionStatus: USER_LOCATION_PERMISSION.DENIED,
            });
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          console.error('Geolocation error:', error);
          const payload = {
            permissionStatus: USER_LOCATION_PERMISSION.DENIED,
          };
          resolve(payload);
        },
        {
          enableHighAccuracy: true,
          timeout: 10 * SECOND_IN_MS,
          maximumAge: 0,
        },
      );
    });
  };

const getIOSLocationPermissionStatus = async () => {
  try {
    return new Promise<USER_LOCATION_PERMISSION>((resolve) => {
      const timeoutId = setTimeout(() => {
        resolve(USER_LOCATION_PERMISSION.DENIED);
      }, 15 * SECOND_IN_MS);

      navigator.geolocation.getCurrentPosition(
        () => {
          clearTimeout(timeoutId);
          resolve(USER_LOCATION_PERMISSION.GRANTED);
        },
        (error) => {
          clearTimeout(timeoutId);
          if (error.code === error.PERMISSION_DENIED) {
            resolve(USER_LOCATION_PERMISSION.DENIED);
          } else {
            resolve(USER_LOCATION_PERMISSION.PROMPT);
          }
        },
        { timeout: 10 * SECOND_IN_MS },
      );
    });
  } catch (error) {
    console.error('iOS permission status error:', error);
    return USER_LOCATION_PERMISSION.DENIED;
  }
};

const getIOSUserCurrentLocation =
  async (): Promise<UserCurrentLocationPayload> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ permissionStatus: USER_LOCATION_PERMISSION.DENIED });
        return;
      }

      const timeoutId = setTimeout(() => {
        resolve({ permissionStatus: USER_LOCATION_PERMISSION.DENIED });
      }, 15 * SECOND_IN_MS);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          if (position && position.coords) {
            resolve({
              coordinates: {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              },
              permissionStatus: USER_LOCATION_PERMISSION.GRANTED,
            });
          } else {
            resolve({ permissionStatus: USER_LOCATION_PERMISSION.DENIED });
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          console.error('iOS geolocation error:', error);
          resolve({
            permissionStatus:
              error.code === error.PERMISSION_DENIED
                ? USER_LOCATION_PERMISSION.DENIED
                : USER_LOCATION_PERMISSION.DENIED,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10 * SECOND_IN_MS,
          maximumAge: 0,
        },
      );
    });
  };

export const useUserLocation = () => {
  if (IS_IOS) {
    return {
      getUserCurrentLocation: getIOSUserCurrentLocation,
      getLocationPermissionStatus: getIOSLocationPermissionStatus,
    };
  }

  return {
    getUserCurrentLocation,
    getLocationPermissionStatus,
  };
};
